import React, { useEffect, useState } from 'react';
import { Box, IconButton, InputBase, Tooltip } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { TOKENS } from './NextGenPortalUI';

/**
 * NexusQuantityStepper — the one quantity control for the Nexus cart AND
 * checkout (27-Aug-2026: extracted from NextGenCart so both pages behave
 * identically).
 *
 *  − / + buttons, plus the number itself is typeable: the typed value is
 *  committed on blur / Enter, clamped to [1, max]. `onChange` is only called
 *  with a valid, different quantity, so the caller can send it straight to the
 *  server (which returns the recomputed cart — the server owns every number).
 */
export const clampQuantity = (raw, max) => {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 1) return 1;
  if (max != null && n > max) return max;
  return n;
};

const NexusQuantityStepper = ({ value, max, disabled, onChange, size = 'small', 'aria-label': ariaLabel = 'Quantity' }) => {
  const [text, setText] = useState(String(value));
  useEffect(() => { setText(String(value)); }, [value]);

  const commit = (raw) => {
    const next = clampQuantity(raw, max);
    setText(String(next));
    if (next !== value) onChange(next);
  };
  const atMax = max != null && value >= max;

  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center',
        border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius.control,
        background: TOKENS.bg.card, overflow: 'hidden',
        opacity: disabled ? 0.6 : 1,
      }}
      role="group"
      aria-label={ariaLabel}
    >
      <IconButton
        size={size}
        aria-label="Decrease quantity"
        disabled={disabled || value <= 1}
        onClick={() => commit(value - 1)}
        sx={{ borderRadius: 0, color: TOKENS.text.muted }}
      >
        <RemoveRoundedIcon sx={{ fontSize: 15 }} />
      </IconButton>
      <InputBase
        value={text}
        disabled={disabled}
        inputMode="numeric"
        inputProps={{ 'aria-label': 'Quantity', min: 1, max: max != null ? max : undefined, style: { textAlign: 'center', padding: 0 } }}
        onChange={(e) => setText(e.target.value.replace(/[^\d]/g, ''))}
        onBlur={() => commit(text)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(text); e.target.blur(); } }}
        sx={{ width: 40, fontWeight: 800, color: TOKENS.text.primary, fontSize: '0.85rem', '& input': { py: 0.5 } }}
      />
      <Tooltip title={atMax ? `Maximum ${max} per order` : ''} disableHoverListener={!atMax}>
        <span>
          <IconButton
            size={size}
            aria-label="Increase quantity"
            disabled={disabled || atMax}
            onClick={() => commit(value + 1)}
            sx={{ borderRadius: 0, color: TOKENS.text.muted }}
          >
            <AddRoundedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default NexusQuantityStepper;
